import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Videocam as VideocamIcon,
  Security as SecurityIcon,
  Sensors as SensorsIcon,
  CellTower as TowerIcon,
  Notifications as NotificationsIcon,
  Speed as SpeedIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const IFTRVSSSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/pi-planning');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to PI Planning Board"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Integrated Fixed Tower (IFT) & Remote Video Surveillance System (RVSS)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            U.S. Border Patrol Surveillance and Detection Systems
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The Integrated Fixed Tower (IFT) and Remote Video Surveillance System (RVSS) are critical components of 
            U.S. Border Patrol's surveillance infrastructure. These systems provide persistent monitoring capabilities 
            along the U.S. borders through advanced sensor integration, camera networks, and real-time detection 
            technologies to identify and respond to illegal border crossings and security threats.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary - Compact Table Format */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="IFT and RVSS System 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What are IFT and RVSS"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Advanced surveillance systems combining <strong>fixed tower infrastructure</strong> with remote video monitoring 
                    capabilities. IFT provides 24/7 persistent surveillance through integrated sensor suites, while RVSS enables 
                    remote monitoring and recording of border activities through strategically placed camera systems.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who operates IFT and RVSS"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Operated by <strong>U.S. Border Patrol (USBP)</strong> under U.S. Customs and Border Protection (CBP). 
                    Systems are monitored by Border Patrol agents at <strong>Remote Video Surveillance System (RVSS) monitoring centers</strong> 
                    and sector command centers. Maintenance and technical support provided by CBP Office of Information and Technology (OIT).
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When were IFT and RVSS deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    IFT program initiated in <strong>2014</strong> as part of the Arizona Border Surveillance Technology Plan. 
                    RVSS has been deployed since the <strong>early 2000s</strong> and continuously upgraded with modern camera 
                    and sensor technologies. Both systems operate <strong>24/7/365</strong> for continuous border monitoring.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where are IFT and RVSS deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Deployed along the <strong>Southwest Border</strong>, primarily in Arizona, Texas, New Mexico, and California. 
                    IFT towers are strategically positioned in remote and high-traffic areas. RVSS cameras are installed at 
                    <strong> ports of entry, between ports, and along known smuggling corridors</strong>. Systems provide coverage 
                    in areas where physical barriers are impractical.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why IFT and RVSS exist"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To provide <strong>persistent, wide-area surveillance</strong> of the border without requiring continuous 
                    physical patrol presence. Enables early detection of illegal crossings, smuggling activities, and security 
                    threats. Improves agent safety by providing situational awareness before deployment. Enhances operational 
                    efficiency through <strong>force multiplication</strong> and intelligent resource allocation.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* System Components */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <TowerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Components
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '2px solid #1976D2',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/ift-component')}
            role="button"
            tabIndex={0}
            aria-label="View Integrated Fixed Tower component details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/ift-component');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TowerIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Integrated Fixed Tower (IFT)
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Permanent surveillance towers equipped with multiple sensor systems for comprehensive area coverage.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Tower Height:</strong> 80-180 feet for extended line-of-sight</Typography></li>
                <li><Typography variant="body2"><strong>Day/Night Cameras:</strong> High-resolution PTZ (Pan-Tilt-Zoom) cameras</Typography></li>
                <li><Typography variant="body2"><strong>Thermal Imaging:</strong> Long-range infrared sensors for night detection</Typography></li>
                <li><Typography variant="body2"><strong>Ground Radar:</strong> Detection of movement up to 7.5 miles</Typography></li>
                <li><Typography variant="body2"><strong>Coverage Area:</strong> Each tower monitors up to 15-20 square miles</Typography></li>
                <li><Typography variant="body2"><strong>Power Systems:</strong> Solar/grid hybrid with battery backup</Typography></li>
              </Box>
              <Typography variant="caption" sx={{ color: '#1976D2', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed component information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '2px solid #2E7D32',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/rvss-component')}
            role="button"
            tabIndex={0}
            aria-label="View RVSS component details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/rvss-component');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VideocamIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Remote Video Surveillance System (RVSS)
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Network of remotely operated cameras providing real-time video feeds to monitoring centers.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Mobile Cameras:</strong> Deployable units for tactical operations</Typography></li>
                <li><Typography variant="body2"><strong>Fixed Installations:</strong> Permanent camera positions at key locations</Typography></li>
                <li><Typography variant="body2"><strong>PTZ Control:</strong> Remote operator control of camera direction and zoom</Typography></li>
                <li><Typography variant="body2"><strong>Video Recording:</strong> 24/7 recording with motion-triggered alerts</Typography></li>
                <li><Typography variant="body2"><strong>Low-Light Capability:</strong> Enhanced night vision and IR illumination</Typography></li>
                <li><Typography variant="body2"><strong>Weather Resistant:</strong> All-weather operation in harsh environments</Typography></li>
              </Box>
              <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed component information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '2px solid #F57C00',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/sensor-integration-component')}
            role="button"
            tabIndex={0}
            aria-label="View Sensor Integration Suite component details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/sensor-integration-component');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SensorsIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Sensor Integration Suite
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Multi-sensor fusion for comprehensive detection and tracking capabilities.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Ground Sensors:</strong> Seismic and acoustic detection systems</Typography></li>
                <li><Typography variant="body2"><strong>Motion Detection:</strong> AI-powered analytics for human/vehicle classification</Typography></li>
                <li><Typography variant="body2"><strong>Radar Systems:</strong> All-weather detection independent of visibility</Typography></li>
                <li><Typography variant="body2"><strong>Sensor Fusion:</strong> Combined data from multiple sources for accuracy</Typography></li>
                <li><Typography variant="body2"><strong>False Alarm Reduction:</strong> Machine learning algorithms filter wildlife</Typography></li>
              </Box>
              <Typography variant="caption" sx={{ color: '#F57C00', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed component information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%', 
              border: '2px solid #D32F2F',
              cursor: 'pointer',
              '&:hover': { 
                boxShadow: 6,
                transform: 'translateY(-4px)',
                transition: 'all 0.3s ease'
              }
            }}
            onClick={() => navigate('/admin/alert-response-component')}
            role="button"
            tabIndex={0}
            aria-label="View Alert and Response System component details"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/admin/alert-response-component');
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <NotificationsIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  Alert and Response System
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Real-time notification and coordination platform for rapid response.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Automated Alerts:</strong> Instant notifications to monitoring centers</Typography></li>
                <li><Typography variant="body2"><strong>GPS Coordinates:</strong> Precise location data for agent dispatch</Typography></li>
                <li><Typography variant="body2"><strong>Video Clips:</strong> Automatic capture and transmission of detection events</Typography></li>
                <li><Typography variant="body2"><strong>Priority Classification:</strong> Threat assessment and resource allocation</Typography></li>
                <li><Typography variant="body2"><strong>Multi-Agency Coordination:</strong> Integration with sector operations centers</Typography></li>
              </Box>
              <Typography variant="caption" sx={{ color: '#D32F2F', fontWeight: 'bold', mt: 2, display: 'block' }}>
                Click to view detailed component information →
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Technical Specifications */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Technical Specifications
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Specification</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>IFT System</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>RVSS System</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Detection Range</strong></TableCell>
              <TableCell>Up to 7.5 miles (radar), 5+ miles (visual)</TableCell>
              <TableCell>1-3 miles depending on camera and terrain</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Coverage Area</strong></TableCell>
              <TableCell>15-20 square miles per tower</TableCell>
              <TableCell>Variable based on deployment configuration</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Camera Resolution</strong></TableCell>
              <TableCell>HD/4K day cameras, thermal imaging</TableCell>
              <TableCell>HD cameras with low-light enhancement</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Operating Hours</strong></TableCell>
              <TableCell>24/7/365 autonomous operation</TableCell>
              <TableCell>24/7/365 with operator monitoring</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Power Source</strong></TableCell>
              <TableCell>Solar/grid hybrid with battery backup</TableCell>
              <TableCell>Grid power or generator with UPS</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Communication</strong></TableCell>
              <TableCell>Microwave, fiber optic, satellite links</TableCell>
              <TableCell>Fiber optic, wireless mesh networks</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Response Time</strong></TableCell>
              <TableCell>Instant alert generation (&lt;5 seconds)</TableCell>
              <TableCell>Real-time video streaming (&lt;2 second latency)</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Environmental Rating</strong></TableCell>
              <TableCell>All-weather, -20°F to 120°F operation</TableCell>
              <TableCell>Weather-resistant, IP66+ rated enclosures</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Operational Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Operational Capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                52
              </Typography>
              <Typography variant="body2" color="text.secondary">
                IFT Towers Deployed
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Primarily in Arizona sectors
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                300+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                RVSS Camera Systems
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Across Southwest Border
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Continuous Monitoring
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                365 days per year
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Key Operational Benefits
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Force Multiplication:</strong> Reduces need for continuous physical patrols</Typography></li>
                <li><Typography variant="body2"><strong>Agent Safety:</strong> Provides situational awareness before deployment</Typography></li>
                <li><Typography variant="body2"><strong>Cost Efficiency:</strong> Lower operational costs than manned patrols</Typography></li>
                <li><Typography variant="body2"><strong>Evidence Collection:</strong> Video documentation for prosecutions</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Wide Area Coverage:</strong> Monitors remote and difficult terrain</Typography></li>
                <li><Typography variant="body2"><strong>Rapid Response:</strong> Enables faster agent deployment to incidents</Typography></li>
                <li><Typography variant="body2"><strong>Intelligence Gathering:</strong> Pattern analysis and trend identification</Typography></li>
                <li><Typography variant="body2"><strong>Multi-Mission Support:</strong> Drug interdiction, human trafficking detection</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Integration with Border Security */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Integration with Border Security Ecosystem
          </Typography>
          <Typography variant="body2" paragraph>
            IFT and RVSS systems are integrated components of CBP's comprehensive border security strategy, working 
            in conjunction with:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Chip label="Automated Targeting System (ATS)" color="primary" sx={{ mb: 1 }} />
              <Typography variant="caption" display="block">Intelligence and risk assessment</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="TECS (Border Database)" color="primary" sx={{ mb: 1 }} />
              <Typography variant="caption" display="block">Law enforcement data integration</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Mobile Surveillance Units" color="primary" sx={{ mb: 1 }} />
              <Typography variant="caption" display="block">Tactical deployment coordination</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Air and Marine Operations" color="primary" sx={{ mb: 1 }} />
              <Typography variant="caption" display="block">Aerial surveillance coordination</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Sector Operations Centers" color="primary" sx={{ mb: 1 }} />
              <Typography variant="caption" display="block">Command and control integration</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Agent Mobile Devices" color="primary" sx={{ mb: 1 }} />
              <Typography variant="caption" display="block">Field agent connectivity</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default IFTRVSSSystemDetail;
